import { Glyph, type GlyphName } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import type { WhiteningActionId, WhiteningSlot } from '@/features/whitening/state'
import { Button, ButtonText } from '@/ui/core/button'
import { useDiscColor, usePanelColor } from '@/ui/core/card-ground'
import { Pressable } from '@/ui/core/pressable'
import { Text } from '@/ui/core/typography'
import * as Linking from 'expo-linking'
import { View } from 'react-native'

/**
 * The one slot at the foot of a card, and the reason both cards keep their shape.
 *
 * Forty-six points, always, whatever stands in it — a button, a parcel, a date, or nothing. The
 * kit card moves through five states over a month or two and the gel card through four; if the
 * foot resized with each of them, the card would be a different object every time somebody
 * opened the page, and the ledger above it would jump. The height is the constant that makes the
 * card one thing with a changing last line.
 *
 * Written as a number rather than a Tailwind step because this rem is fourteen: `h-12` is 42 and
 * there is no step that is 46.
 */
const SLOT = 46

/**
 * The space between a row's disc and its words: the same 14 the billing rows and the profile's
 * set theirs at, so the three read as one kind of row wherever they are met.
 */
const ROW_GAP = 14

/**
 * A parcel, or a date: a row, led by a mark on a disc the way every other row in this app is.
 *
 * It was a bare label for a while — the disc came off with the "Track this parcel" line beneath
 * it — and what was left read as a caption in a box rather than as a row. The disc is the same
 * `h-10` circle the billing rows and the profile's use, and it fits the slot's fixed 46 points
 * with five and a half above and below, so the row's height and radius are untouched; only the
 * label moves right.
 *
 * The mark's own colour is ink on either card, as every row's is. The panel it sits in and the
 * disc behind it both come off the card's ground, which the row is told by the context rather
 * than by a prop — the same one the visit card's rows read.
 */
const SlotRow = ({
	label,
	glyph,
	onPress,
}: {
	label: string
	glyph: GlyphName
	/** Absent for a note, and for a parcel whose carrier we have no page for. */
	onPress?: () => void
}) => {
	const panel = usePanelColor()
	const disc = useDiscColor()

	return (
		<Pressable
			onPress={onPress}
			disabled={!onPress}
			accessibilityRole={onPress ? 'button' : undefined}
			accessible={!!onPress}
			accessibilityLabel={label}
			className="flex-1 flex-row items-center rounded-brand px-4"
			style={{ gap: ROW_GAP, backgroundColor: panel }}
		>
			<View
				className="h-10 w-10 items-center justify-center rounded-full"
				style={{ backgroundColor: disc }}
			>
				<Glyph name={glyph} size={18} color={colors.ink} />
			</View>
			<Text size="body1" weight="bold" numberOfLines={1} className="flex-1">
				{label}
			</Text>
			{/* The arrow is the whole of the invitation. A second line saying "track this parcel"
			    explained what an arrow already says, next to a number that already says what it is. */}
			{!!onPress && <Glyph name="arrow" size={15} color={colors.ink} />}
		</Pressable>
	)
}

export const CardSlot = ({
	slot,
	isBusy = false,
	onAction,
}: {
	slot?: WhiteningSlot
	isBusy?: boolean
	onAction?: (action: WhiteningActionId) => void
}) => {
	// Lifted out of the JSX because a narrowing on a parameter does not survive into a closure,
	// and the press handler is one.
	const parcel = slot?.kind === 'tracking' ? slot.url : undefined

	return (
		<View style={{ height: SLOT }}>
			{slot?.kind === 'button' && (
				<Button
					variant="primary"
					fullWidth
					isLoading={isBusy}
					onPress={() => onAction?.(slot.action)}
					style={{ height: SLOT }}
				>
					<ButtonText>{slot.label}</ButtonText>
				</Button>
			)}
			{/* Drawn, and deliberately not pressable: the date on its face is the answer to the
		    question pressing it would have asked, which is better than a button that appears out
		    of nowhere on the morning it starts working. */}
			{slot?.kind === 'dormant' && (
				<Button variant="primary" fullWidth disabled style={{ height: SLOT }}>
					<ButtonText>{slot.label}</ButtonText>
				</Button>
			)}
			{slot?.kind === 'note' && <SlotRow label={slot.label} glyph={slot.glyph} />}
			{/* The van, always: a tracking row is a parcel in transit by definition, so it is the
			    one mark here the state module does not have to choose. */}
			{slot?.kind === 'tracking' && (
				<SlotRow
					label={slot.label}
					glyph="van"
					onPress={parcel ? () => Linking.openURL(parcel) : undefined}
				/>
			)}
		</View>
	)
}
